// AuditLogsSuper.jsx - Version adaptée aux couleurs ISS
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { 
    Search, Loader2, ChevronLeft, ChevronRight, 
    ShieldCheck, Clock, User, Globe, Database, Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import { APP_CONFIG } from '../../../config/appConfig';

export default function AuditLogsSuper() {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, totalPages: 0, totalElements: 0 });
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchLogs = useCallback(async (page = 0) => {
        if (!token) {
            console.warn('Token non disponible');
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            const url = `${APP_CONFIG.BASE_URL}/api/v1/config/monitor/logs?page=${page}&size=50${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`;
            const response = await fetch(url, { 
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                } 
            });
            
            if (!response.ok) {
                const text = await response.text();
                console.error('Réponse erreur:', text);
                if (response.status === 401) {
                    throw new Error('Session expirée - Veuillez vous reconnecter');
                }
                if (response.status === 403) {
                    throw new Error('Accès refusé - Rôle SUPERADMIN requis');
                }
                throw new Error(`Erreur HTTP ${response.status}: ${text.substring(0, 100)}`);
            }
            
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('Le serveur a retourné une réponse non-JSON');
            }
            
            const data = await response.json();
            setLogs(data.content || []);
            setPagination({ 
                page: data.number || 0, 
                totalPages: data.totalPages || 0,
                totalElements: data.totalElements || 0
            });
        } catch (err) {
            console.error("Erreur de chargement des logs:", err.message);
            setError(err.message);
            toast.error(`Erreur: ${err.message}`);
            setLogs([]);
        } finally {
            setIsLoading(false);
        }
    }, [token, searchTerm]);

    useEffect(() => {
        if (token) {
            fetchLogs();
        }
    }, [fetchLogs, token]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (token) {
                fetchLogs(0);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, fetchLogs, token]);

    if (!token) {
        return (
            <div className="p-4 text-center font-serif">
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-lg p-4">
                    <p className="text-[#212529] font-serif">Authentification requise pour voir les logs d'audit</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-4 text-left font-serif text-[#495057]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-2xl shadow-sm border border-[#e9ecef]">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 flex-1 w-full md:w-auto">
                    <h1 className="text-xl font-serif font-black text-[#212529] shrink-0 uppercase tracking-tight flex items-center gap-2">
                        <ShieldCheck size={20} style={{ color: '#009640' }} /> 
                        Journaux d'Audit
                    </h1>
                    <div className="relative w-full md:max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#adb5bd]" size={16} />
                        <input 
                            type="text" 
                            placeholder="Chercher par utilisateur, endpoint ou statut..." 
                            className="w-full pl-10 pr-4 py-2 bg-[#f8f9fa] border border-[#e9ecef] rounded-xl text-sm font-serif focus:ring-2 focus:ring-[#009640] focus:border-[#009640] outline-none transition-all"
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <button 
                    onClick={() => fetchLogs(0)} 
                    className="p-2.5 bg-[#f8f9fa] rounded-xl hover:bg-[#e8f5ed] transition-all duration-200"
                    disabled={isLoading}
                    style={{ color: '#009640' }}
                >
                    <Filter size={18} />
                </button>
            </div>

            {error && (
                <div className="bg-[#fef3e8] border border-[#ef7c00] rounded-xl p-3 text-[#212529] text-sm font-serif">
                    <strong>Erreur :</strong> {error}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-[#e9ecef] overflow-hidden relative min-h-[500px]">
                {isLoading && (
                    <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center backdrop-blur-[1px]">
                        <Loader2 className="animate-spin" size={28} style={{ color: '#009640' }} />
                    </div>
                )}
                
                {!isLoading && logs.length === 0 && !error && (
                    <div className="flex flex-col items-center justify-center py-12 text-[#adb5bd]">
                        <Database size={48} className="mb-3 opacity-50" />
                        <p className="text-sm font-serif">Aucun log d'audit trouvé</p>
                    </div>
                )}
                
                {logs.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left table-auto">
                            <thead>
                                <tr className="bg-[#f8f9fa] border-b border-[#e9ecef] text-[10px] font-serif font-black text-[#6c757d] uppercase tracking-wider">
                                    <th className="px-3 py-3">Horodatage</th>
                                    <th className="px-3 py-3">Utilisateur</th>
                                    <th className="px-3 py-3">Méthode</th>
                                    <th className="px-3 py-3">Endpoint</th>
                                    <th className="px-3 py-3">Statut</th>
                                    <th className="px-3 py-3 text-right">Durée</th>
                                </tr>
                            </thead>
                            <tbody className="text-[11px] font-serif">
                                {logs.map((log, index) => (
                                    <tr key={log.id || index} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                                        <td className="px-3 py-2 whitespace-nowrap text-[#6c757d] font-serif">
                                            <div className="flex items-center gap-1.5">
                                                <Clock size={12} className="text-[#adb5bd]" /> {log.timestamp}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 font-bold text-[#212529] font-serif">
                                            <div className="flex items-center gap-1.5 uppercase">
                                                <User size={12} className="text-[#adb5bd]" /> {log.username || 'Système'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-serif font-bold ${
                                                log.method === 'GET' ? 'bg-[#e8f5ed] text-[#009640]' :
                                                log.method === 'POST' ? 'bg-[#fef3e8] text-[#ef7c00]' :
                                                log.method === 'PUT' ? 'bg-[#fef3e8] text-[#ef7c00]' :
                                                'bg-[#f8d7da] text-[#dc3545]'
                                            }`}>
                                                {log.method}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-[#495057] truncate max-w-[200px] font-serif" title={log.endpoint}>
                                            <div className="flex items-center gap-1.5">
                                                <Globe size={12} className="text-[#adb5bd]" /> {log.endpoint}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`font-serif font-bold ${log.statusCode >= 400 ? 'text-[#ef7c00]' : 'text-[#009640]'}`}>
                                                {log.statusCode}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right text-[#6c757d] italic font-serif">
                                            {log.durationMs}ms
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pagination.totalPages > 0 && (
                    <div className="p-3 bg-[#f8f9fa] border-t border-[#e9ecef] flex flex-col sm:flex-row justify-between items-center gap-2 px-4 text-[10px] font-serif">
                        <span className="font-bold text-[#6c757d] uppercase tracking-wider">
                            {pagination.totalElements} entrées - Page {pagination.page + 1}/{pagination.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button 
                                disabled={pagination.page === 0 || isLoading} 
                                onClick={() => fetchLogs(pagination.page - 1)} 
                                className="p-1.5 bg-white border border-[#e9ecef] rounded-lg hover:bg-[#f8f9fa] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={14}/>
                            </button>
                            <button 
                                disabled={pagination.page + 1 >= pagination.totalPages || isLoading} 
                                onClick={() => fetchLogs(pagination.page + 1)} 
                                className="p-1.5 bg-white border border-[#e9ecef] rounded-lg hover:bg-[#f8f9fa] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={14}/>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}